import React from 'react';
import AsyncSelect from 'react-select/async';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { reactSelectDefaultStyles } from '../../utils/reactSelectDefaultStyles';
import { SelectOption } from '../SelectOption';
import { IBatchDetailsObject } from './BatchDetails';

async function loadAvailableObjects(input?: string) {
    let options: SelectOption[] = [];

    if (input && input.length >= 3) {
        const url = '/api/objects';
        const result = await fetchGet(url, { objectName: input }, ['NO_DATA_AVAILABLE']);
        options = result.json.items.map((object: { id: string; name: string }) => ({ value: object.id, label: object.name }));
    }
    return options;
}

type ObjectWithBatch = { id: string; batch: { name: string; status: string } };

type BatchDetailsAddObjectProps = { onClose: () => void; onAdd: (objectId: string) => void };

export function BatchDetailsAddObject(props: BatchDetailsAddObjectProps) {
    const { onClose, onAdd } = props;

    const translate = useTranslate();
    const [selectedObject, setSelectedObject] = React.useState<IBatchDetailsObject | null>(null);
    const [batchesInObject, setBatchesInObject] = React.useState<ObjectWithBatch[]>([]);

    function onFormSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (selectedObject) {
            onAdd(selectedObject.id);
        }
    }

    async function onSelectChange(selectedOption: SelectOption | null): Promise<void> {
        if (selectedOption !== null) {
            const objectId = selectedOption.value;
            const selectedObjectResponse = await fetchGet(`/api/objects/${objectId}`);
            setSelectedObject(selectedObjectResponse.json as IBatchDetailsObject);
            const batchesInObjectResponse = await fetchGet(`/api/objects/${objectId}/batches`);
            setBatchesInObject(batchesInObjectResponse.json as ObjectWithBatch[]);
        } else {
            setSelectedObject(null);
            setBatchesInObject([]);
        }
    }

    return (
        <div id="popup_element" style={{ minHeight: '300px' }}>
            <form method="post" action="/" id="admin-form" onSubmit={onFormSubmit}>
                <div className="panel">
                    <div className="panel-heading">
                        <span className="panel-title">{translate('common.addItem', translate('common.object'))}</span>
                    </div>
                    <div className="panel-body">
                        <div className="section">
                            <AsyncSelect
                                id="name"
                                name="name"
                                backspaceRemoves={true}
                                isClearable={true}
                                styles={reactSelectDefaultStyles}
                                inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'off' }}
                                loadOptions={loadAvailableObjects}
                                onChange={onSelectChange}
                                valueKey="value"
                                labelKey="label"
                                placeholder={translate('common.searchItem', translate('common.object'))}
                                autoFocus
                            />

                            {selectedObject && (
                                <div className="mt20">
                                    <div>
                                        <span className="mr20 fw600">{translate('common.name')}:</span>
                                        {selectedObject.name}
                                    </div>
                                    <div>
                                        <span className="mr20 fw600">{translate('common.location')}:</span>
                                        {selectedObject.location}
                                    </div>
                                    <div>
                                        <span className="mr20 fw600">Type:</span>
                                        {selectedObject.objectTypeName}
                                    </div>
                                    <div>
                                        <span className="mr20 fw600">{translate('common.composition')}:</span>
                                        {translate(`inspectionStandard.${selectedObject.inspectionStandardType}`)}
                                    </div>
                                    <div>
                                        <span className="mr20 fw600">{translate('object.usage')}:</span>
                                        {selectedObject.usage}
                                    </div>
                                    {batchesInObject.length > 0 && (
                                        <div>
                                            <span className="mr20 fw600">In batch:</span>
                                            <ul>
                                                {batchesInObject.map((item, i) => (
                                                    <li key={i}>
                                                        {item.batch.name} ({item.batch.status})
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className=" text-right mt10">
                            <button type="button" onClick={onClose} className="btn btn-sm fw600 ml10" id="close">
                                {translate('common.close')}
                            </button>
                            <button type="submit" className="btn btn-primary btn-sm fw600 ml10" id="close-element-popup">
                                {translate('common.addItem', translate('common.object'))}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
